import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    createTheme,
    FormControlLabel,
    FormLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { Formik, Form, FormikProps } from 'formik';
import { DevelopmentalHistoryModel } from '../../../Models/DevelopmentalHistory/DevelopmentalHistoryModel.model';
import {
    fetchDevelopmentalHistoryInfo,
    fetchDevelopmentalHistoryInfoByMemberId,
    saveDevelopmentalHistoryInfo,
    setDevelopmentalHistoryInfo,
} from '../../../store/developmentalHistorySlice.slice';
import { copyObject, highlightStyle } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { NO, YES } from '../../../utils/constants';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Abuse_History_Developmental_History_Mental_Health_Treatment_History.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '85%',
        },
    },
};

const OTHER = 'Other';
const yesNoList = [{ Answer: YES }, { Answer: NO }];

const contextualNonpersonalFactorsList = [
    { Answer: 'Not applicable' },
    { Answer: 'Divorce in the family' },
    { Answer: 'A recent death' },
    { Answer: 'Natural disaster' },
    { Answer: 'Economic downturn' },
    { Answer: 'Other' },
];

const DevelopmentalHistory: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { developmentalHistoryInfo } = useSelector((state: RootState) => state.developmentalHistorySlice);
    const isSaving = isSavingEngagementDraft;
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const developmentalHistoryIsComplete = (info: DevelopmentalHistoryModel) => {
        return Boolean(info.ContextualNonpersonalFactors?.trim()?.length > 0);
    };

    const handleOnConfirmToNext = (developmentalHistoryInfo: DevelopmentalHistoryModel, isComplete = false) => {
        const copy = copyObject(developmentalHistoryInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveDevelopmentalHistoryInfo(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveDevelopmentalHistoryInfo(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setDevelopmentalHistoryInfo(new DevelopmentalHistoryModel()));
        dispatch(gotoNextMenu());
    };

    const handleOnClickSave = () => {
        setShowSaveAlert(true);
    };
    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    useEffect(() => {
        if (engagementDraft?.DevelopmentalHistoryId) {
            dispatch(fetchDevelopmentalHistoryInfo(engagementDraft.DevelopmentalHistoryId));
        } else {
            dispatch(fetchDevelopmentalHistoryInfoByMemberId(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Formik initialValues={developmentalHistoryInfo} onSubmit={handleSubmit} enableReinitialize={true}>
            {({ values, setFieldValue, errors, handleChange }: FormikProps<DevelopmentalHistoryModel>) => {
                const handleFieldChange = (e: React.ChangeEvent<any>) => {
                    if (e.target.name == 'ContextualNonpersonalFactors') {
                        setFieldValue('IsAIContextualNonpersonalFactors', false);
                    }
                    if (e.target.name == 'PertinentDevelopmentalIssues') {
                        setFieldValue('IsAIPertinentDevelopmentalIssues', false);
                    }
                    if (e.target.name == 'MothersPregnancyHistory') {
                        setFieldValue('IsAIMothersPregnancyHistory', false);
                    }
                    if (e.target.name == 'InfancyHistory') {
                        setFieldValue('IsAIInfancyHistory', false);
                    }
                    if (e.target.name == 'PreschoolHistory') {
                        setFieldValue('IsAIPreschoolHistory', false);
                    }
                    if (e.target.name == 'ChildhoodHistory') {
                        setFieldValue('IsAIChildhoodHistory', false);
                    }
                    if (e.target.name == 'AdolescentHistory') {
                        setFieldValue('IsAIAdolescentHistory', false);
                    }
                    if (e.target.name == 'PertinentSexualIssuesConcerns') {
                        setFieldValue('IsAIPertinentSexualIssuesConcerns', false);
                    }
                    setIsChanged(true);
                    handleChange(e);
                };
                return (
                    <Form>
                        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                                <Grid>
                                    {isSaving ? (
                                        <Box display="flex" justifyContent="center" className="loader-container">
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <>
                                            <Grid container spacing={2} sx={classes.root}>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                                        <Typography variant="h6">Developmental History</Typography>
                                                        <Tooltip title="Developmental History Help Document">
                                                            <InfoIcon
                                                                color="primary"
                                                                fontSize="small"
                                                                cursor="pointer"
                                                                onClick={() => window.open(`${HelpPage}`, '_blank')}
                                                            />
                                                        </Tooltip>
                                                    </Stack>
                                                    {isBHDiagnostic && (
                                                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                                            * Required for BH Diagnostic
                                                        </Typography>
                                                    )}
                                                    <hr />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel required={isBHDiagnostic} sx={isBHDiagnostic ? highlightStyle : undefined}>
                                                        {AssessmentQuestions.DevelopmentalHistory_ContextualNonpersonalFactors}
                                                        {values.IsAIContextualNonpersonalFactors && isAIIcon()}
                                                    </FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <RadioGroup
                                                        row
                                                        name="ContextualNonpersonalFactors"
                                                        value={values.ContextualNonpersonalFactors || ''}
                                                        onChange={handleFieldChange}
                                                    >
                                                        {contextualNonpersonalFactorsList.map((type, index) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={`answer-label-${index}`}
                                                                    value={type.Answer}
                                                                    control={<Radio />}
                                                                    label={type.Answer}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                    {values.ContextualNonpersonalFactors === OTHER && (
                                                        <TextField
                                                            label="Please specify"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            margin="normal"
                                                            name="ContextualNonpersonalFactorsOther"
                                                            value={values.ContextualNonpersonalFactorsOther || ''}
                                                            onChange={handleFieldChange}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <hr className="hr-lite" />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.DevelopmentalHistory_PertinentDevelopmentalIssues}</FormLabel>
                                                    {values.IsAIPertinentDevelopmentalIssues && isAIIcon()}
                                                </Grid>
                                                <Grid size={6}>
                                                    <RadioGroup
                                                        row
                                                        name="PertinentDevelopmentalIssues"
                                                        value={values.PertinentDevelopmentalIssues || ''}
                                                        onChange={handleFieldChange}
                                                    >
                                                        {yesNoList.map((type, index) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={`answer-label-${index}`}
                                                                    value={type.Answer}
                                                                    control={<Radio />}
                                                                    label={type.Answer}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </Grid>

                                                <Grid size={12}>
                                                    <Collapse in={values.PertinentDevelopmentalIssues === YES} timeout="auto" unmountOnExit>
                                                        <Grid container spacing={2}>
                                                            <Grid size={12}>
                                                                <FormLabel>Pertinent Developmental Issues, if applicable:</FormLabel>
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <FormLabel>{AssessmentQuestions.DevelopmentalHistory_MothersPregnancyHistory}</FormLabel>
                                                                {values.IsAIMothersPregnancyHistory && isAIIcon()}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <RadioGroup
                                                                    row
                                                                    name="MothersPregnancyHistory"
                                                                    value={values.MothersPregnancyHistory || ''}
                                                                    onChange={handleFieldChange}
                                                                >
                                                                    {yesNoList.map((type, index) => {
                                                                        return (
                                                                            <FormControlLabel
                                                                                key={`answer-label-${index}`}
                                                                                value={type.Answer}
                                                                                control={<Radio />}
                                                                                label={type.Answer}
                                                                            />
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                                {values.MothersPregnancyHistory === YES && (
                                                                    <TextField
                                                                        label={AssessmentQuestions.DevelopmentalHistory_MothersPregnancyHistory}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        margin="normal"
                                                                        name="MothersPregnancyHistoryDetail"
                                                                        value={values.MothersPregnancyHistoryDetail || ''}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                )}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <FormLabel>{AssessmentQuestions.DevelopmentalHistory_Infancy01}</FormLabel>
                                                                {values.IsAIInfancyHistory && isAIIcon()}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <RadioGroup
                                                                    row
                                                                    name="InfancyHistory"
                                                                    value={values.InfancyHistory || ''}
                                                                    onChange={handleFieldChange}
                                                                >
                                                                    {yesNoList.map((type, index) => {
                                                                        return (
                                                                            <FormControlLabel
                                                                                key={`answer-label-${index}`}
                                                                                value={type.Answer}
                                                                                control={<Radio />}
                                                                                label={type.Answer}
                                                                            />
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                                {values.InfancyHistory === YES && (
                                                                    <TextField
                                                                        label="Please specify issues for infancy history"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        margin="normal"
                                                                        name="InfancyHistoryDetail"
                                                                        value={values.InfancyHistoryDetail || ''}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                )}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <FormLabel>{AssessmentQuestions.DevelopmentalHistory_Preschool}</FormLabel>
                                                                {values.IsAIPreschoolHistory && isAIIcon()}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <RadioGroup
                                                                    row
                                                                    name="PreschoolHistory"
                                                                    value={values.PreschoolHistory || ''}
                                                                    onChange={handleFieldChange}
                                                                >
                                                                    {yesNoList.map((type, index) => {
                                                                        return (
                                                                            <FormControlLabel
                                                                                key={`answer-label-${index}`}
                                                                                value={type.Answer}
                                                                                control={<Radio />}
                                                                                label={type.Answer}
                                                                            />
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                                {values.PreschoolHistory === YES && (
                                                                    <TextField
                                                                        label={AssessmentQuestions.DevelopmentalHistory_PreschoolDetail}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        margin="normal"
                                                                        name="PreschoolHistoryDetail"
                                                                        value={values.PreschoolHistoryDetail || ''}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                )}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <FormLabel>{AssessmentQuestions.DevelopmentalHistory_Childhood}</FormLabel>
                                                                {values.IsAIChildhoodHistory && isAIIcon()}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <RadioGroup
                                                                    row
                                                                    name="ChildhoodHistory"
                                                                    value={values.ChildhoodHistory || ''}
                                                                    onChange={handleFieldChange}
                                                                >
                                                                    {yesNoList.map((type, index) => {
                                                                        return (
                                                                            <FormControlLabel
                                                                                key={`answer-label-${index}`}
                                                                                value={type.Answer}
                                                                                control={<Radio />}
                                                                                label={type.Answer}
                                                                            />
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                                {values.ChildhoodHistory === YES && (
                                                                    <TextField
                                                                        label={AssessmentQuestions.DevelopmentalHistory_ChildhoodDetail}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        margin="normal"
                                                                        name="ChildhoodHistoryDetail"
                                                                        value={values.ChildhoodHistoryDetail || ''}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                )}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <FormLabel>{AssessmentQuestions.DevelopmentalHistory_Adolescence}</FormLabel>
                                                                {values.IsAIAdolescentHistory && isAIIcon()}
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <RadioGroup
                                                                    row
                                                                    name="AdolescentHistory"
                                                                    value={values.AdolescentHistory || ''}
                                                                    onChange={handleFieldChange}
                                                                >
                                                                    {yesNoList.map((type, index) => {
                                                                        return (
                                                                            <FormControlLabel
                                                                                key={`answer-label-${index}`}
                                                                                value={type.Answer}
                                                                                control={<Radio />}
                                                                                label={type.Answer}
                                                                            />
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                                {values.AdolescentHistory === YES && (
                                                                    <TextField
                                                                        label={AssessmentQuestions.DevelopmentalHistory_AdolescenceHistoryDetail}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        margin="normal"
                                                                        name="AdolescentHistoryDetail"
                                                                        value={values.AdolescentHistoryDetail || ''}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Collapse>
                                                </Grid>

                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <hr className="hr-lite" />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.DevelopmentalHistory_SexualHistory}</FormLabel>
                                                    {values.IsAIPertinentSexualIssuesConcerns && isAIIcon()}
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label=""
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        placeholder="Please explain"
                                                        margin="normal"
                                                        name="PertinentSexualIssuesConcerns"
                                                        value={values.PertinentSexualIssuesConcerns || ''}
                                                        onChange={handleFieldChange}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Grid justifyContent="flex-end" className="content-footer">
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="secondary"
                                                    sx={{ marginRight: 2 }}
                                                    disabled={isSaving}
                                                    onClick={() => dispatch(gotoPreviousMenu())}
                                                    startIcon={<ArrowBackIosIcon />}
                                                >
                                                    Previous
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={isSaving}
                                                    endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                                    onClick={handleOnClickSave}
                                                >
                                                    Next
                                                </Button>
                                            </Grid>
                                        </>
                                    )}

                                    {Boolean(developmentalHistoryIsComplete(values) && values.CreatedBy && !isChanged) ? (
                                        <AlertDialog
                                            open={showSaveAlert}
                                            title={`Developomental History`}
                                            buttonsDirection="column-reverse"
                                            showBottomCenter={true}
                                            cancelText="Go Back"
                                            skipText={`Developomental History Assessment Not Reviewed`}
                                            okText={`Agree With Developomental History Assessment`}
                                            onConfirm={() => handleOnConfirmToNext(values, true)}
                                            onSkip={handleOnConfirmSkip}
                                            onClose={() => setShowSaveAlert(false)}
                                            isLoading={isSaving}
                                            message={`Last Developomental History assessment completed on ${(() => {
                                                const d = toDate(values.UpdatedDate ?? values.CreatedDate);
                                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                            })()}.`}
                                        />
                                    ) : Boolean(developmentalHistoryIsComplete(values)) ? (
                                        <AlertDialog
                                            open={showSaveAlert}
                                            title="Developomental History"
                                            cancelText="Go Back"
                                            okText="Confirm"
                                            onConfirm={() => handleOnConfirmToNext(values, true)}
                                            onSkip={handleOnConfirmSkip}
                                            showBottomCenter={true}
                                            onClose={() => setShowSaveAlert(false)}
                                            isLoading={isSaving}
                                            message="I confirm and acknowledge that Developomental History is completed."
                                        />
                                    ) : (
                                        <AlertDialog
                                            open={showSaveAlert}
                                            title="Developomental History"
                                            showBottomCenter={true}
                                            cancelText="Go Back"
                                            skipText="Skip and continue"
                                            okText="Confirm"
                                            onConfirm={() => handleOnConfirmToNext(values, false)}
                                            onSkip={handleOnConfirmSkip}
                                            onClose={() => setShowSaveAlert(false)}
                                            isLoading={isSaving}
                                            renderBody={() =>
                                                BHDiagnosticConfirmationBody({
                                                    assessmentName: 'Developmental History',
                                                    diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                                })
                                            }
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Form>
                );
            }}
        </Formik>
    );
};
export default DevelopmentalHistory;
